/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable no-param-reassign */
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {useSelector} from 'react-redux'

/**
 * 新接口 /live/saada_resolve/get_task_info 的 data（见文档：hide_show、hide_list、
 * task_stats、record_new_red_dot、icon_list[].record_red_dot）映射为旧 get_pearl_wallet_info 形态，
 * 供现有页面继续使用 simple_wallet / hide_withdraw / hide_list / refund_red_dot 等字段。
 * 若线上仍偶发旧字段，用 `raw.simple_wallet` 等与主路径做兼容。
 */
export function normalizePearlInfoPayload(raw: any) {
  if (raw == null || typeof raw !== 'object') return raw
  const {hide_show, task_stats, icon_list, record_new_red_dot, ...rest} = raw
  const stats = task_stats ?? raw?.simple_wallet
  const simple_wallet =
    stats && typeof stats === 'object'
      ? {
          ...stats,
          overage: stats?.overage ?? stats?.completed,
          auditing_overage: stats?.auditing_overage ?? stats?.processing,
        }
      : undefined
  const mappedIconList = Array.isArray(icon_list)
    ? icon_list.map((item: any) => ({
        ...item,
        refund_red_dot: item?.refund_red_dot ?? item?.record_red_dot,
      }))
    : icon_list
  return {
    ...rest,
    hide_withdraw: hide_show ?? raw?.hide_withdraw,
    hide_list: Array.isArray(raw?.hide_list) ? raw.hide_list : [],
    simple_wallet,
    icon_list: mappedIconList,
    refund_new_red_dot: record_new_red_dot ?? raw?.refund_new_red_dot,
  }
}

// [saada珍珠钱包首页] 实际请求 /live/saada_resolve/get_task_info，数据在 normalize 中还原为旧结构
export const get_pearl_wallet_info = createAsyncThunk(
  'Saada/saadaWalletHome/get_pearl_wallet_info',
  async (p: any, store: any) => {
    const info = store.getState().env.deviceinfo
    const resp = await http.post(
      '/live/saada_resolve/get_task_info',
      {
        ...info,
        mid: info.h_m,
        ...p,
      },
      {
        xcErrorToast: true,
      }
    )
    return normalizePearlInfoPayload(resp.data.data)
  }
)

// [查询用户7天内的失败订单数量以及最新一笔订单失败状态和原因] http://wiki.xunlei.cn/pages/viewpage.action?pageId=156941139
export const query_failed_order_info = createAsyncThunk(
  'Saada/saadaWalletHome/query_failed_order_info',
  async (p: {kind: number}, store: any) => {
    const info = store.getState().env.deviceinfo
    const resp = await http.post(
      '/live/saada_settle/query_failed_order_info',
      {
        ...info,
        mid: info.h_m,
        ...p,
      },
      {
        xcErrorToast: true,
      }
    )
    return resp.data.data
  }
)

// 钱包信息类型定义
interface SimpleWallet {
  overage: string // 可提取的余额
  frozen_overage: string // 冻结、不可提取、审核通过的余额
  auditing_overage: string // 审核中的余额
  total: string // 总余额
}

// 图标列表项类型定义
interface IconListItem {
  name: string
  img: string
  url: string
  sign_status: string
  refund_red_dot: boolean
}

// 失败订单信息类型定义
interface FailedOrderInfo {
  failed_order_amount: number // 最近7天的失败订单数量
  failed_order_remind: {
    failed_reason_by_channel_partner: string // 合作方返回的打款失败原因
    possible_problems_by_saada: string // Saada识别到可能的问题
    suggestion: string // Saada对用户的建议
  }
  is_remind: boolean // 是否需要弹窗提示
}

// 钱包首页状态类型定义
interface InitialStateType {
  hide_withdraw?: boolean // 是否隐藏提现相关的信息
  hide_list?: number[] // 需隐藏的 Tab：1 转金币 / 2 代收 / 3 提现
  simple_wallet?: SimpleWallet // 钱包信息
  icon_list?: IconListItem[] // 图标列表
  failed_order?: FailedOrderInfo // 失败订单信息
}

const initialState: InitialStateType = {}

export function useSaadaWalletHome() {
  return useSelector((store) => store.Animal_Beaver)
}

export const SaadaWalletHomeSlice = createSlice({
  name: 'Animal_Beaver',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(get_pearl_wallet_info.fulfilled, (state, action) => {
      Object.assign(state, action.payload)
    })
    builder.addCase(query_failed_order_info.fulfilled, (state, action) => {
      Object.assign(state, {failed_order: action.payload})
    })
  },
})

export default SaadaWalletHomeSlice.reducer
